<template>
  <div id="shop-app" class="border border-solid d-theme-border-grey-light rounded relative overflow-hidden body-module-height z-20">
    <div class="shop-header mb-4">
      <div class="flex md:items-end items-center justify-between flex-wrap">
        <!-- TOGGLE SIDEBAR BUTTON -->
        <feather-icon class="inline-flex lg:hidden cursor-pointer mr-4" icon="FilterIcon" @click.stop="toggleFilterSidebar" />

        <p class="lg:inline-flex hidden font-semibold algolia-filters-label">
          {{ $t('pages.shop.filters') }}
        </p>

        <div class="flex justify-between items-end flex-grow">
          <p class="font-semibold md:block hidden">
            {{ products.length }} {{ $t('pages.shop.resultsFound') }}
          </p>

          <div class="flex flex-wrap">
            <!-- ITEM VIEW - GRID/LIST -->
            <div>
              <feather-icon
                  icon="GridIcon"
                  @click="currentItemView='item-grid-view'"
                  class="p-2 shadow-drop rounded-lg d-theme-dark-bg cursor-pointer"
                  :svgClasses="{'text-primary stroke-current': currentItemView == 'item-grid-view'}" />
              <feather-icon
                  icon="ListIcon"
                  @click="currentItemView='item-list-view'"
                  class="p-2 ml-4 shadow-drop rounded-lg d-theme-dark-bg cursor-pointer hidden sm:inline-flex"
                  :svgClasses="{'text-primary stroke-current': currentItemView == 'item-list-view'}" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div id="shop-content-container" class="content-scroll-area">
      <vs-sidebar
          class="items-no-padding vs-sidebar-rounded background-absolute"
          parent="#shop-content-container"
          :click-not-close="clickNotClose"
          :hidden-background="clickNotClose"
          v-model="isFilterSidebarActive">

        <div class="p-6 filter-container">
          <h6 class="font-bold mb-4"> {{ $t('pages.shop.categories') }}</h6>
          <div>
            <ul>
              <li v-for="category in productCategories" :key="category.id" class="mb-2 flex items-center justify-between">
                <vs-checkbox v-model="productCategoryFilter" class="ml-0" :vs-value="category.id">
                  {{ dataLang(category).name }}
                </vs-checkbox>
              </li>
            </ul>
          </div>

          <vs-divider />

          <div class="flex justify-center">
            <vs-button class="w-full" @click="productCategoryFilter=[]" :disabled="this.productCategoryFilter.length === 0">
              {{ $t('pages.shop.remove_filers') }}
            </vs-button>
          </div>
        </div>
      </vs-sidebar>

      <!-- RIGHT COL -->
      <div :class="{ 'sidebar-spacer-with-margin px-2': clickNotClose }">

        <!-- SEARCH BAR -->
        <div class="mb-4 flex flex-wrap">
          <!-- SEARCH INPUT -->
          <div class="relative min-w-1/2 flex flex-grow pr-2">
            <vs-input class="w-full vs-input-shadow-drop vs-input-no-border d-theme-input-dark-bg" placeholder="Search here"
                      :value="this.textFilter" @input="search" @keyup.esc="search('')" size="large" />

            <!-- SEARCH LOADING -->
            <p :hidden="!this.isSearching" class="mt-4 text-grey">
              <feather-icon icon="ClockIcon" svgClasses="w-4 h-4" class="mr-2 align-middle" />
              <span>Loading...</span>
            </p>

            <!-- SEARCH ICON -->
            <div class="absolute top-0 right-0 py-4 px-6" v-show="!this.textFilter">
              <feather-icon icon="SearchIcon" svgClasses="h-6 w-6" />
            </div>

            <!-- CLEAR INPUT ICON -->
            <div class="absolute top-0 right-0 py-4 px-6" v-show="this.textFilter">
              <feather-icon icon="XIcon" svgClasses="h-6 w-6 cursor-pointer" @click="search('')" />
            </div>
          </div>
        </div>
        <!-- SEARCH RESULT -->

        <!-- UPGRADE CASE -->
        <case-upgrade />
        <!-- UPGRADE CASE -->

        <template v-if="this.isLoaded">
          <!-- GRID VIEW -->
          <template v-if="currentItemView == 'item-grid-view'">
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div class="w-full" v-for="p in products" :key="p.id">
                <item-grid-view :product-data="p">
                  <!-- SLOT: ACTION BUTTONS -->
                  <template #action-buttons="{product}">
                    <div class="flex flex-wrap">
                      <!-- PRIMARY BUTTON: ADD-TO/VIEW-IN CART -->
                      <div
                          class="item-view-secondary-action-btn bg-primary p-3 flex flex-grow items-center justify-center text-white cursor-pointer"
                          @click="cartButtonClicked(product)">
                        <feather-icon icon="ShoppingBagIcon" svgClasses="h-4 w-4" />

                        <span class="text-sm font-semibold ml-2" v-if="isInCart(product.id)">{{ $t('pages.shop.viewInCart') | uppercase }}</span>
                        <span class="text-sm font-semibold ml-2" v-else>{{ $t('pages.shop.addToCart') | uppercase }}</span>
                      </div>

                      <!-- PRIMARY BUTTON: ADD-TO/VIEW-IN CART -->
                      <div v-if="isInCart(product.id)"
                          class="item-view-primary-action-btn bg-danger p-3 flex flex-grow items-center justify-center cursor-pointer"
                           @click.stop="removeItemFromCart(product)">

                        <feather-icon icon="Trash2Icon" svgClasses="h-4 w-4" />
                        <span class="text-sm font-semibold ml-2">{{ $t('pages.shop.removeFromCart') | uppercase }}</span>
                      </div>
                    </div>
                  </template>
                </item-grid-view>
              </div>
            </div>
          </template>
          <template v-else> <!-- LIST VIEW -->
            <div class="items-list-view mb-base" v-for="p in products" :key="p.id">
              <item-list-view :product-data="p">
                <!-- SLOT: ACTION BUTTONS -->
                <template #action-buttons="{product}">
                  <div
                      class="item-view-secondary-action-btn bg-primary p-3 rounded-lg flex flex-grow items-center justify-center text-white cursor-pointer"
                      @click="cartButtonClicked(product)">
                    <feather-icon icon="ShoppingBagIcon" svgClasses="h-4 w-4" />

                    <span class="text-sm font-semibold ml-2" v-if="isInCart(product.id)">{{ $t('pages.shop.viewInCart') | uppercase }}</span>
                    <span class="text-sm font-semibold ml-2" v-else>{{ $t('pages.shop.addToCart') | uppercase }}</span>
                  </div>

                  <div v-if="isInCart(product.id)"
                      class="item-view-primary-action-btn border border-danger hover:bg-danger mt-2 p-3 rounded-lg flex flex-grow items-center justify-center text-white cursor-pointer"
                      @click.stop="removeItemFromCart(product)">
                    <feather-icon icon="Trash2Icon" svgClasses="h-4 w-4" />
                    <span class="text-sm font-semibold ml-2">{{ $t('pages.shop.removeFromCart') | uppercase }}</span>
                  </div>
                </template>
                <!-- END SLOT: ACTION BUTTONS -->
              </item-list-view>
            </div>
          </template>
        </template>

        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="3" v-model="currentx"></vs-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ItemGridView from "./ecommerce/components/ItemGridView";
import ItemListView from "./ecommerce/components/ItemListView";
import ShopService from "@/services/Shop.service";
import {showErrorNotify} from "@/helpers/functionsHelpers";
import ProductCategoryService from "@/services/ProductCategory.service";
import CaseUpgrade from "@/views/ecommerce/components/CaseUpgrade";
import ProfileService from "@/services/Profile.service";

export default {
  components: {
    CaseUpgrade,
    //ItemGridView: () => import('./ecommerce/ItemGridView.vue'),
    ItemGridView,
    ItemListView
  },
  data () {
    return {
      // Filter Sidebar
      textFilter: '',
      productCategoryFilter: [],
      isFilterSidebarActive: true,
      clickNotClose: true,
      currentItemView: 'item-grid-view',
      isSearching: false,
      currentx: 1,

      itemsPerPage: 6, //it is a constant, just to easy to change if needed
      isLoaded: false,
    }
  },
  watch: {
    windowWidth() {
      this.setSidebarWidth()
    },
    getFilteredProducts() {
      this.currentx = 1;
    }
  },
  computed: {
    productCategories() {
      return this.$store.getters['ProductCategory/listCategories'];
    },
    getFilteredProducts() {
      const products = this.$store.getters['Shop/listProductsFromShop'];
      let productsFiltered = products.slice();
      let _this = this;
      if (this.productCategoryFilter.length > 0) {
        productsFiltered = products.filter((p) => {
          return _this.productCategoryFilter.includes(p.productCategory);
        });
      }

      productsFiltered.sort((a, b) => {
        if (a.code > b.code) {
          return 1;
        }
        else if (a.code < b.code) {
          return -1;
        } else {
          return 0;
        }
      });
      return productsFiltered;
    },
    totalPages() {
      return Math.ceil(this.getFilteredProducts.length / this.itemsPerPage);
    },
    products() {
      let start_item = (this.currentx-1) * this.itemsPerPage;
      let sliced_products = this.getFilteredProducts.slice(start_item, start_item + this.itemsPerPage);
      return sliced_products;
    },

    // GRID VIEW
    isInCart () {
      return (itemId) => this.$store.getters['Shop/isInCart'](itemId)
    },
    windowWidth () { return this.$store.state.windowWidth }
  },
  methods: {
    dataLang(item) {
      const locale = this.$i18n.locale;
      return item[locale];
    },
    search(text) {
      console.debug('Searching => ', text);
      this.textFilter = text;
    },
    setSidebarWidth () {
      if (this.windowWidth < 992) {
        this.isFilterSidebarActive = this.clickNotClose = false;
      } else {
        this.isFilterSidebarActive = this.clickNotClose = true;
      }
    },

    // GRID VIEW - ACTIONS
    toggleFilterSidebar () {
      if (this.clickNotClose) return
      this.isFilterSidebarActive = !this.isFilterSidebarActive
    },
    addItemInCart (product_id) {
      this.$store.dispatch('Shop/addItemInCart', product_id);
    },
    removeItemFromCart (product) {
      this.$store.dispatch('Shop/toggleItemInCart', product.id);
    },
    cartButtonClicked (product) {
      this.isInCart(product.id) ? this.$router.push({name: 'shop-checkout'}) : this.addItemInCart(product.id);
    },

    /**
     * REMOVE ITEM UPGRADE
     */
    removeCaseUpgrade() {
      this.$store.dispatch('Shop/resetCaseUpgrade');
    }
  },
  created () {
    this.setSidebarWidth();
  },
  mounted() {
    const promiseProducts = ShopService.getProductsFromShop().then((response) => {
      this.$store.dispatch('Shop/setProductsFromShop', response);
      return Promise.resolve(true);
    }, (error) => {
      showErrorNotify(this, error);
      return Promise.reject(error);
    });

    const promiseCategories = ProductCategoryService.getProductCategories().then((response) => {
      return this.$store.dispatch('ProductCategory/setCategories', response).then(() => {
        return Promise.resolve(true);
      });
    }, (error) => {
      showErrorNotify(this, error);
      return Promise.reject(error);
    });

    const promiseInstructor = ProfileService.getInstructor().then((response) => {
      return this.$store.dispatch('Profile/setInstructor', response);
    });

    const promiseSleeves = ShopService.fetchSleevesData().then((response) => {
      this.$store.commit('Shop/SET_SLEEVES', response);
      return Promise.resolve(true);
    })

    Promise.all([promiseProducts, promiseCategories, promiseInstructor, promiseSleeves]).then((results) => {
      this.isLoaded = true;
      console.debug('Pagina produtos carregada => ', results);
    });
  }
}
</script>

<style lang="scss">

#shop-app {
  .shop-header {
    .algolia-filters-label {
      width: calc(var(--max-sidebar-width) + 2.4rem);
    }
  }
  #shop-content-container {
    .vs-sidebar {
      position: relative;
      float: left;
      width: var(--max-sidebar-width);
    }

    &.content-scroll-area {
      height: calc(100% - 4rem);
    }
  }

  .algolia-search-input-right-aligned-icon {
    padding: 1rem 1.5rem;
  }

  .item-view-primary-action-btn {
    color: #2c2c2c !important;
    background-color: #f6f6f6;
    min-width: 50%;
  }

  .item-view-secondary-action-btn {
    min-width: 50%;
  }
}

@media (min-width: 992px) {
  .vs-sidebar-rounded {
    .vs-sidebar {
      border-radius: .5rem;
    }

    .vs-sidebar--items {
      border-radius: .5rem;
    }
  }
}

@media (max-width: 992px) {
  #shop-content-container {
    .vs-sidebar {
      position: absolute !important;
      float: none !important;
    }
  }
}

</style>
